// src/components/item-builder/ItemPickerModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  Search,
  CheckSquare,
  Square,
  Filter,
  PlusCircle,
  Pencil,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { listItems } from "../../services/api/items";
import { toast } from "react-toastify";
import ItemQuickCreateModal from "./ItemQuickCreateModal";
import { htmlToText } from "../../utils/htmlToText";
import { httpRequest } from "../../utils/httpRequest";
import AiItemGeneratorModal from "./AiItemGeneratorModal";
// 🔹 مدال تأیید حذف (هم‌استایل با چیزهایی که قبلاً داشتیم)
const ConfirmDeleteModal = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  loading,
  onCancel,
  onConfirm,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl p-5">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {title || "آیا مطمئن هستید؟"}
        </h3>
        {description && (
          <p className="text-sm text-gray-600 mb-4">{description}</p>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-lg border text-sm hover:bg-gray-50"
            disabled={loading}
          >
            {cancelLabel || "انصراف"}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-60"
            disabled={loading}
          >
            {loading ? "..." : confirmLabel || "حذف"}
          </button>
        </div>
      </div>
    </div>
  );
};

const KIND_OPTIONS = [
  "mcq_single",
  "mcq_multi",
  "mcq_matrix",
  "true_false",
  "matching",
  "drag_to_group",
  "image_matching",
  "ordering",
  "ordering_weighted",
  "cloze_text",
  "cloze_regex",
  "word_bank",
  "short_text",
  "essay",
  "numeric",
  "drag_drop_item_target",
  "sentence_builder",
  "select_sentence",
  "passage_mcq",
  "audio_prompt",
  "writing_prompt",
  // "writing_edit_rewrite",
  "file_upload",
  "audio_gate",
  "speaking_integrated",
  "c_test",
  "sentence_reconstruction",
];

export default function ItemPickerModal({
  isOpen,
  onClose,
  token,
  onPick, // (selectedItemsArray) => void
  categoryId,
  examId,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("published");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  // 👇 حالت مدال آیتم (ساخت / ویرایش)
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemModalMode, setItemModalMode] = useState("create"); // "create" | "edit"
  const [itemToEdit, setItemToEdit] = useState(null);

  // 🔥 حالت حذف آیتم
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    fetchItems();
    setSelectedIds(new Set());
  }, [isOpen, kind, status]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await listItems(token, { q: query, kind, status });
      const arr = res?.data?.data || res?.data || [];
      setItems(arr);
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectedList = useMemo(
    () => items.filter((it) => selectedIds.has(it._id)),
    [items, selectedIds],
  );

  const handleApply = () => {
    if (selectedList.length === 0) {
      toast.error(t("item_picker.toasts.nothing_selected"));
      return;
    }
    onPick?.(selectedList);
    onClose?.();
  };

  const handleAskDelete = (it) => {
    setDeleteTarget(it);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?._id) return;
    try {
      setDeleting(true);
      await httpRequest(
        "DELETE",
        `/item/${deleteTarget._id}`,
        {},
        { "x-access-token": token },
      );

      toast.success(t("item_picker.delete_success", "سؤال با موفقیت حذف شد."));

      // حذف از لیست و انتخاب‌ها
      setItems((prev) => prev.filter((x) => x._id !== deleteTarget._id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(deleteTarget._id);
        return next;
      });
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-4xl rounded-2xl shadow-xl p-4 md:p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold">
            {t("item_picker.title")}
          </h2>
          <button
            className="p-2 rounded-lg hover:bg-gray-100"
            onClick={onClose}
            aria-label="close"
          >
            <X />
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="flex items-center border rounded-lg px-3 py-2">
            <Search className="w-4 h-4 opacity-60" />
            <input
              className="flex-1 px-2 outline-none"
              placeholder={t("item_picker.search_ph")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchItems()}
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 opacity-60" />
            <select
              className="flex-1 border rounded-lg px-3 py-2"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="">{t("item_picker.kind_any")}</option>
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {t(`item_kinds.${k}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="published">
                {t("item_picker.status_published")}
              </option>
              <option value="draft">{t("item_picker.status_draft")}</option>
              <option value="">{t("item_picker.status_any")}</option>
            </select>
          </div>
        </div>

        <div className="mb-3 flex gap-2">
          <button
            onClick={fetchItems}
            className="px-3 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            {t("item_picker.search_btn")}
          </button>
          <button
            onClick={() => {
              setItemToEdit(null);
              setItemModalMode("create");
              setItemModalOpen(true);
            }}
            className="px-3 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 text-sm"
          >
            {t("item_picker.create_new")}
          </button>
          <button
            onClick={() => setAiModalOpen(true)}
            className="px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm"
          >
            تولید با هوش مصنوعی
          </button>
        </div>

        {/* List */}
        <div className="max-h-[50vh] overflow-auto border rounded-xl">
          {loading ? (
            <div className="p-4 text-sm text-gray-500">
              {t("item_picker.loading")}
            </div>
          ) : items.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">
              {t("item_picker.empty")}
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((it) => {
                const checked = selectedIds.has(it._id);
                const title =
                  it?.stem?.fa || it?.stem?.en || t("item_picker.no_title");

                const safeTitle = htmlToText(title);

                return (
                  <li
                    key={it._id}
                    className="p-3 flex items-center gap-3 justify-between"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <button
                        onClick={() => toggleSelect(it._id)}
                        className="p-2 rounded-lg hover:bg-gray-50"
                        aria-label={checked ? "unselect" : "select"}
                      >
                        {checked ? (
                          <CheckSquare className="w-5 h-5 text-purple-600" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                      </button>

                      <div className="flex-1 max-w-full">
                        <p
                          dir="rtl"
                          className="text-sm font-medium text-gray-900 truncate"
                        >
                          {safeTitle}
                        </p>

                        <div className="text-xs text-gray-500">
                          {t(`item_kinds.${it.kind}`)} •{" "}
                          {t("item_picker.points_n", { n: it.points ?? 1 })}
                        </div>
                      </div>
                    </div>

                    {/* 👇 اکشن‌ها: ادیت + حذف */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setItemToEdit(it);
                          setItemModalMode("edit");
                          setItemModalOpen(true);
                        }}
                        className="px-2 py-1 text-xs rounded-md border text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1"
                      >
                        <Pencil className="w-3 h-3" />
                        {t("common.edit", "ویرایش")}
                      </button>

                      <button
                        onClick={() => handleAskDelete(it)}
                        className="px-2 py-1 text-xs rounded-md border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        {t("common.delete", "حذف")}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className={`mt-4 flex ${isRtl ? "justify-start" : "justify-end"}`}>
          <button
            onClick={handleApply}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2"
          >
            <PlusCircle className="w-4 h-4" />
            {t("item_picker.add_to_section")}
          </button>
        </div>

        <AiItemGeneratorModal
          isOpen={aiModalOpen}
          onClose={() => setAiModalOpen(false)}
          token={token}
          categoryId={categoryId}
          examId={examId}
          defaultLanguage="auto"
          onGenerated={(generatedItems) => {
            // 1) توی لیست دوباره بکش که داخل UI دیده بشه
            fetchItems().then(() => {
              // 2) همه آیتم‌های ساخته شده رو انتخاب کن
              setSelectedIds((prev) => {
                const next = new Set(prev);
                (generatedItems || []).forEach((it) => {
                  if (it?._id) next.add(it._id);
                });
                return next;
              });

              // ✅ 3) خیلی مهم: همونجا به parent بده تا SAVE انجام بشه
              if (Array.isArray(generatedItems) && generatedItems.length > 0) {
                onPick?.(generatedItems);
                onClose?.(); // بستن ItemPickerModal
              }
            });

            setAiModalOpen(false);
          }}
        />

        {/* مدال ساخت/ویرایش آیتم */}
        <ItemQuickCreateModal
          isOpen={itemModalOpen}
          onClose={() => setItemModalOpen(false)}
          token={token}
          mode={itemModalMode}
          item={itemToEdit}
          onCreated={(doc) => {
            // بعد از ایجاد/ویرایش، لیست را رفرش کن و آیتم را در صورت امکان انتخاب کن
            fetchItems();
          }}
        />

        {/* 🧨 مدال تأیید حذف آیتم */}
        <ConfirmDeleteModal
          open={deleteConfirmOpen}
          title={t("item_picker.delete_title", "حذف سؤال")}
          description={t(
            "item_picker.delete_desc",
            "آیا از حذف این سؤال مطمئن هستید؟ این عمل قابل بازگشت نیست.",
          )}
          confirmLabel={t("common.delete", "حذف")}
          cancelLabel={t("common.cancel", "انصراف")}
          loading={deleting}
          onCancel={() => {
            if (deleting) return;
            setDeleteConfirmOpen(false);
            setDeleteTarget(null);
          }}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </div>
  );
}
